import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { updateMonitoringPassword } from "@/lib/api";

export const monitoringProviderLabel = (provider) =>
  provider === "shinemonitor" ? "ShineMonitor" : "Polycab";

export function MonitoringPasswordDialog({ account, plantId, open, onOpenChange, onSuccess }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const provider = monitoringProviderLabel(account?.provider);

  const changeOpen = (next) => {
    if (submitting) return;
    if (!next) {
      setPassword("");
      setError("");
    }
    onOpenChange(next);
  };

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await updateMonitoringPassword({ plantId, password });
      setPassword("");
      onOpenChange(false);
      await onSuccess?.();
    } catch (requestError) {
      const status = requestError?.response?.status;
      const detail = requestError?.response?.data?.detail;
      if (status === 409 && detail === "login_rejected") {
        setError(`That password was rejected by ${provider}. Check it on the portal and try again.`);
      } else if (status === 409 && detail === "login_not_rejected") {
        setError("This login is not paused, nothing to update.");
      } else if (status === 429) {
        setError("Too many attempts, try again in an hour.");
      } else {
        setError("Could not update the login. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogContent className="max-w-sm" data-testid="monitoring-password-dialog">
        <DialogHeader>
          <DialogTitle>Update {provider} login</DialogTitle>
          <DialogDescription>
            Enter the new password for {account?.username || `your ${provider} account`}.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <label className="grid gap-1.5 text-sm font-medium text-brand-ink">
            <span>New {provider} password</span>
            <Input
              required
              type="password"
              autoComplete="new-password"
              value={password}
              disabled={submitting}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={submitting} onClick={() => changeOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !password}>
              {submitting ? "Submitting..." : "Submit"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default MonitoringPasswordDialog;
